import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import { toastSuccess, toastError } from "../utils/toast";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  HeartIcon,
} from "@heroicons/react/24/outline";

const normalizePrivilegeSelection = (keys) => {
  const uniqueKeys = [...new Set((keys || []).filter(Boolean))];
  // Stato legacy o risposta API non coerente: Anziano prevale sempre.
  return uniqueKeys.includes("anziano")
    ? uniqueKeys.filter((key) => key !== "servitore_ministero")
    : uniqueKeys;
};

const GestioneVolontari = () => {
  const { user, activeCongregazione } = useAuth();
  const [allVolontari, setAllVolontari] = useState([]); // Pagina corrente dal server
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showPrivilegesModal, setShowPrivilegesModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [privilegesCatalog, setPrivilegesCatalog] = useState([]);
  const [selectedPrivileges, setSelectedPrivileges] = useState([]);
  const [privilegesLoading, setPrivilegesLoading] = useState(false);
  const [volunteerPrivilegesLoading, setVolunteerPrivilegesLoading] = useState(false);
  const [privilegesInitialized, setPrivilegesInitialized] = useState(false);
  const [editingVolontario, setEditingVolontario] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [eligibleSpouses, setEligibleSpouses] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileDetailVolunteer, setMobileDetailVolunteer] = useState(null);
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    password: "",
    confermaPassword: "",
    sesso: "",
    stato: "attivo",
    ruolo: "volontario",
    coniuge_id: null,
    service_leader: false,
    postazioni_attivo: true,
  });
  const [filters, setFilters] = useState({
    stato: "",
    sesso: "",
    search: "",
  });
  const [searchInput, setSearchInput] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });
  const [summary, setSummary] = useState({
    totale: 0,
    uomini: 0,
    donne: 0,
    attivi: 0,
  });
  const [invitePushLoadingId, setInvitePushLoadingId] = useState(null);
  const assignmentCode = useMemo(() => {
    const rawCode =
      editingVolontario?.congregazione_codice ||
      activeCongregazione?.codice ||
      user?.congregazione_codice ||
      "";
    if (!rawCode) {
      return "";
    }
    return rawCode.toString().padStart(3, "0");
  }, [
    editingVolontario?.congregazione_codice,
    activeCongregazione?.codice,
    user?.congregazione_codice,
    ]);

  const spouseOptions = eligibleSpouses;
  const filteredPrivileges = useMemo(() => {
    if (!formData.sesso) return [];
    const canBePublicSpeaker =
      selectedPrivileges.includes("anziano") ||
      selectedPrivileges.includes("servitore_ministero");
    return (privilegesCatalog || [])
      .filter(
        (privilegio) =>
          privilegio.attivo !== false &&
          (privilegio.sesso_ammesso === "A" ||
            privilegio.sesso_ammesso === formData.sesso) &&
          (privilegio.key !== "oratore_pubblico" || canBePublicSpeaker)
      )
      .sort((a, b) => (a.ordine || 0) - (b.ordine || 0));
  }, [privilegesCatalog, formData.sesso, selectedPrivileges]);
  const selectedPrivilegesSet = useMemo(
    () => new Set(selectedPrivileges),
    [selectedPrivileges]
  );

  useEffect(() => {
    const hasRequiredAppointment =
      selectedPrivileges.includes("anziano") ||
      selectedPrivileges.includes("servitore_ministero");
    if (
      !hasRequiredAppointment &&
      selectedPrivileges.includes("oratore_pubblico")
    ) {
      setSelectedPrivileges((current) =>
        current.filter((key) => key !== "oratore_pubblico")
      );
    }
  }, [selectedPrivileges]);

  // Controllo autorizzazione - spostato dopo gli hooks
  const isAuthorized =
    user?.ruolo === "admin" || user?.ruolo === "super_admin";

  const requireCongregazioneSelezionata = useCallback(() => {
    if (user?.ruolo === "super_admin" && !activeCongregazione?.id) {
      toastError(
        "Seleziona una congregazione attiva dalla pagina Congregazioni per continuare."
      );
      return false;
    }
    return true;
  }, [user, activeCongregazione]);

  const fetchVolontari = useCallback(async () => {
    try {
      if (!requireCongregazioneSelezionata()) {
        setAllVolontari([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const response = await api.get("/volontari", {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          stato: filters.stato || undefined,
          sesso: filters.sesso || undefined,
          search: filters.search || undefined,
        },
      });

      if (response.data.volontari) {
        setAllVolontari(response.data.volontari);
      } else {
        setAllVolontari(response.data);
      }
      setPagination((prev) => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        pages: response.data.pagination?.pages || 1,
      }));
      setSummary(
        response.data.summary || {
          totale: response.data.pagination?.total || 0,
          uomini: 0,
          donne: 0,
          attivi: 0,
        }
      );
    } catch (error) {
      console.error("Errore nel caricamento dei volontari:", error);
      toastError("Errore nel caricamento dei volontari");
    } finally {
      setLoading(false);
    }
  }, [
    requireCongregazioneSelezionata,
    pagination.page,
    pagination.limit,
    filters.stato,
    filters.sesso,
    filters.search,
  ]);

  useEffect(() => {
    fetchVolontari();
  }, [fetchVolontari]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const search = searchInput.trim();
      setFilters((prev) =>
        prev.search === search ? prev : { ...prev, search }
      );
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 300);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  // Popola il form quando si seleziona un volontario per la modifica
  useEffect(() => {
    if (editingVolontario) {
      const hasPrefetchedPrivileges = Array.isArray(editingVolontario.privilegi);
      setSelectedPrivileges(
        hasPrefetchedPrivileges
          ? normalizePrivilegeSelection(editingVolontario.privilegi)
          : []
      );
      setPrivilegesInitialized(hasPrefetchedPrivileges);
      setFormData({
        nome: editingVolontario.nome,
        cognome: editingVolontario.cognome,
        email: editingVolontario.email || "",
        telefono: editingVolontario.telefono || "",
        password: "",
        confermaPassword: "",
        sesso: editingVolontario.sesso,
        stato: editingVolontario.stato,
        ruolo: editingVolontario.ruolo,
        coniuge_id: editingVolontario.coniuge_id || null,
        service_leader: editingVolontario.service_leader || false,
        postazioni_attivo:
          editingVolontario.postazioni_attivo ?? true,
      });
      // Fetch eligible spouses when editing
      fetchEligibleSpouses(editingVolontario.id);
    } else {
      resetForm();
      setEligibleSpouses([]);
    }
  }, [editingVolontario]);

  const getStatusColor = (stato) => {
    return stato === "attivo"
      ? "bg-green-100 text-green-800"
      : "bg-red-100 text-red-800";
  };

  const getSessoIcon = (sesso) => {
    return sesso === "M" ? "👨" : "👩";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Mai";
    const date = new Date(dateString);
    return date.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSearch = (searchTerm) => {
    // Aggiorna immediatamente il valore dell'input
    setSearchInput(searchTerm);
  };

  const resetForm = () => {
    setFormData({
      nome: "",
      cognome: "",
      email: "",
      telefono: "",
      password: "",
      confermaPassword: "",
      sesso: "",
      stato: "attivo",
      ruolo: "volontario",
      coniuge_id: null,
      service_leader: false,
      postazioni_attivo: true,
    });
    setSelectedPrivileges([]);
    setPrivilegesInitialized(false);
  };

  // Fetch eligible spouses for a volunteer
  const fetchEligibleSpouses = async (volontarioId) => {
    if (!volontarioId) {
      setEligibleSpouses([]);
      return;
    }
    try {
      const response = await api.get(`/volontari/coniugi-disponibili/${volontarioId}`);
      setEligibleSpouses(response.data || []);
    } catch (error) {
      console.error('Errore nel recupero dei coniugi disponibili:', error);
      toastError("Impossibile caricare i coniugi: " + (error.response?.data?.message || error.message));
      setEligibleSpouses([]);
    }
  };

  const fetchEligibleSpousesForCreate = async (sesso) => {
    if (!sesso) {
      setEligibleSpouses([]);
      return;
    }
    try {
      const response = await api.get("/volontari/coniugi-disponibili", {
        params: { sesso },
      });
      setEligibleSpouses(response.data || []);
    } catch (error) {
      console.error("Errore nel recupero dei coniugi disponibili:", error);
      toastError(
        "Impossibile caricare i coniugi: " +
          (error.response?.data?.message || error.message)
      );
      setEligibleSpouses([]);
    }
  };

  const fetchPrivilegesCatalog = useCallback(async () => {
    setPrivilegesLoading(true);
    try {
      const response = await api.get("/privilegi");
      setPrivilegesCatalog(response.data?.privilegi || []);
    } catch (error) {
      console.error("Errore nel recupero dei privilegi:", error);
      toastError("Impossibile caricare i privilegi");
    } finally {
      setPrivilegesLoading(false);
    }
  }, []);

  const fetchVolunteerPrivileges = useCallback(async (volontarioId) => {
    if (!volontarioId) return;
    setVolunteerPrivilegesLoading(true);
    try {
      const response = await api.get(`/volontari/${volontarioId}/privilegi`);
      const keys = (response.data?.privilegi || []).map((privilegio) => privilegio.key);
      setSelectedPrivileges(normalizePrivilegeSelection(keys));
      setPrivilegesInitialized(true);
    } catch (error) {
      console.error("Errore nel recupero dei privilegi volontario:", error);
      toastError("Impossibile caricare i privilegi del volontario");
      setSelectedPrivileges([]);
      setPrivilegesInitialized(true);
    } finally {
      setVolunteerPrivilegesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrivilegesCatalog();
  }, [fetchPrivilegesCatalog]);

  const filterPrivilegesForSesso = useCallback(
    (keys, sesso) => {
      if (!sesso || !privilegesCatalog.length) {
        return [];
      }
      const allowedKeys = new Set(
        privilegesCatalog
          .filter(
            (privilegio) =>
              privilegio.attivo !== false &&
              (privilegio.sesso_ammesso === "A" ||
                privilegio.sesso_ammesso === sesso)
          )
          .map((privilegio) => privilegio.key)
      );
      return normalizePrivilegeSelection(
        (keys || []).filter((key) => allowedKeys.has(key))
      );
    },
    [privilegesCatalog]
  );

  useEffect(() => {
    if (!showPrivilegesModal) return;

    if (!privilegesCatalog.length && !privilegesLoading) {
      fetchPrivilegesCatalog();
    }

    if (
      editingVolontario?.id &&
      !Array.isArray(editingVolontario.privilegi) &&
      !privilegesInitialized &&
      !volunteerPrivilegesLoading
    ) {
      fetchVolunteerPrivileges(editingVolontario.id);
    } else if (!editingVolontario?.id) {
      setPrivilegesInitialized(true);
    }
  }, [
    showPrivilegesModal,
    privilegesCatalog.length,
    fetchPrivilegesCatalog,
    fetchVolunteerPrivileges,
    editingVolontario?.id,
    editingVolontario?.privilegi,
    privilegesInitialized,
    privilegesLoading,
    volunteerPrivilegesLoading,
  ]);

  const togglePrivilege = useCallback((privilegeKey) => {
    setSelectedPrivileges((current) => {
      if (current.includes(privilegeKey)) {
        return current.filter((key) => key !== privilegeKey);
      }
      if (privilegeKey === "anziano") {
        return [...current.filter((key) => key !== "servitore_ministero"), privilegeKey];
      }
      if (privilegeKey === "servitore_ministero") {
        return [...current.filter((key) => key !== "anziano"), privilegeKey];
      }
      return [...current, privilegeKey];
    });
    setPrivilegesInitialized(true);
  }, []);

  const handleFormChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "sesso") {
        if (value !== "M") {
          next.service_leader = false;
        }
        if (value !== prev.sesso) {
          next.coniuge_id = null;
        }
      }
      return next;
    });
    if (field === "sesso") {
      setSelectedPrivileges((prev) => filterPrivilegesForSesso(prev, value));
      setPrivilegesInitialized(true);
      if (!editingVolontario) {
        fetchEligibleSpousesForCreate(value);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (saving) {
      return;
    }

    if (!requireCongregazioneSelezionata()) {
      return;
    }

    // Validazione password per nuovi volontari
    if (!editingVolontario) {
      if (formData.password.length < 6) {
        toastError("La password deve essere di almeno 6 caratteri");
        return;
      }
      if (formData.password !== formData.confermaPassword) {
        toastError("Le password non coincidono");
        return;
      }
    }

    // Validazione password per aggiornamento
    if (
      editingVolontario &&
      formData.password &&
      formData.password.length < 6
    ) {
      toastError("La nuova password deve essere di almeno 6 caratteri");
      return;
    }

    try {
      setSaving(true);
      if (editingVolontario) {
        // Per l'aggiornamento, rimuovi la password se è vuota
        const updateData = { ...formData };
        if (!updateData.password || updateData.password.trim() === "") {
          delete updateData.password;
        }
        delete updateData.confermaPassword; // Rimuovi sempre confermaPassword
        if (privilegesInitialized) {
          updateData.privilegi = selectedPrivileges;
        }
        if (user?.ruolo === "super_admin" && activeCongregazione?.id) {
          updateData.congregazione_id = activeCongregazione.id;
        }
        await api.put(`/volontari/${editingVolontario.id}`, updateData);
        toastSuccess("Volontario aggiornato con successo");
      } else {
        // Crea nuovo volontario
        const createData = { ...formData };
        delete createData.confermaPassword; // Rimuovi confermaPassword
        createData.privilegi = selectedPrivileges;
        if (user?.ruolo === "super_admin") {
          if (!activeCongregazione?.id) {
            toastError(
              "Seleziona una congregazione attiva prima di creare un volontario."
            );
            return;
          }
          createData.congregazione_id = activeCongregazione.id;
        }
        const { data: createdVolontario } = await api.post(
          "/volontari",
          createData
        );
        if (createData.coniuge_id && createdVolontario?.id) {
          const updateData = { ...createData };
          delete updateData.password;
          await api.put(`/volontari/${createdVolontario.id}`, updateData);
        }
        toastSuccess("Volontario creato con successo");
      }

      setShowModal(false);
      setEditingVolontario(null);
      setShowPrivilegesModal(false);
      resetForm();
      fetchVolontari();
    } catch (error) {
      console.error("Errore nel salvataggio:", error);
      toastError(error.response?.data?.message || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleDelete = async (volontarioId) => {
    if (!requireCongregazioneSelezionata()) {
      return;
    }

    // Trova il volontario da eliminare
    const volontarioToDelete = (allVolontari || []).find(
      (v) => v.id === volontarioId
    );

    // Impedisci l'eliminazione dell'admin corrente
    if (
      volontarioToDelete?.ruolo === "admin" &&
      volontarioToDelete?.id === user?.id
    ) {
      toastError("Non puoi eliminare il tuo account admin");
      return;
    }

    if (!window.confirm("Sei sicuro di voler eliminare questo volontario?")) {
      return;
    }

    setDeletingId(volontarioId);

    // Ottimizzazione: rimuovi immediatamente dalla lista locale
    const updatedVolontari = (allVolontari || []).filter(
      (v) => v.id !== volontarioId
    );
    setAllVolontari(updatedVolontari);

    try {
      await api.delete(`/volontari/${volontarioId}`);
      toastSuccess("Volontario eliminato con successo");

      await fetchVolontari();
    } catch (error) {
      console.error("Errore nell'eliminazione:", error);
      toastError("Errore nell'eliminazione del volontario");

      // Ripristina la lista in caso di errore
      fetchVolontari();
    } finally {
      setDeletingId(null);
    }
  };

  const handleInvitePush = async (volontarioId) => {
    if (!isAuthorized) return;
    setInvitePushLoadingId(volontarioId);
    try {
      await api.post("/push/invite", {
        volontario_id: volontarioId,
        message: "Attiva le notifiche push per ricevere aggiornamenti.",
      });
      toastSuccess("Invito push registrato per il volontario");
    } catch (error) {
      console.error("Errore invio invito push:", error);
      toastError(
        error.response?.data?.message ||
          "Errore durante l'invito alle notifiche push"
      );
    } finally {
      setInvitePushLoadingId(null);
    }
  };

  const openEditModal = (volontario) => {
    setEditingVolontario(volontario);
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingVolontario(null);
    resetForm();
    setEligibleSpouses([]);
    setShowModal(true);
  };

  // Funzione per gestire l'export
  const handleExport = async (format) => {
    if (!requireCongregazioneSelezionata()) {
      return;
    }

    try {
      const response = await api.get(`/volontari/export/${format}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], {
        type: format === 'json' ? 'application/json' : 'text/csv'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `volontari.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toastSuccess(`Volontari esportati in formato ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Errore nell\'esportazione:', error);
      toastError('Errore nell\'esportazione dei volontari');
    }
  };

  // Funzione per gestire l'importazione
  const handleImport = async () => {
    if (!importFile) {
      toastError('Seleziona un file da importare');
      return;
    }

    if (!requireCongregazioneSelezionata()) {
      return;
    }

    const formData = new FormData();
    formData.append('file', importFile);
    if (user?.ruolo === 'super_admin' && activeCongregazione?.id) {
      formData.append('congregazione_id', activeCongregazione.id);
    }

    setImporting(true);
    try {
      const response = await api.post('/volontari/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toastSuccess(response.data.message);
      
      // Mostra dettagli dell'importazione se ci sono errori
      if (response.data.results && response.data.results.errors.length > 0) {
        console.log('Errori di importazione:', response.data.results.errors);
        toastError(`${response.data.results.errors.length} errori durante l'importazione. Controlla la console per i dettagli.`);
      }

      setShowImportModal(false);
      setImportFile(null);
      fetchVolontari(); // Ricarica la lista
    } catch (error) {
      console.error('Errore nell\'importazione:', error);
      toastError(error.response?.data?.message || 'Errore nell\'importazione dei volontari');
    } finally {
      setImporting(false);
    }
  };

  // Funzione per gestire la selezione del file
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = ['application/json', 'text/csv'];
      const validExtensions = ['.json', '.csv'];
      
      const isValidType = validTypes.includes(file.type) || 
                         validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      
      if (isValidType) {
        setImportFile(file);
      } else {
        toastError('Formato file non supportato. Utilizzare JSON o CSV.');
        event.target.value = '';
      }
    }
  };

  // La pagina e i filtri sono applicati dal server: nessun record viene perso
  // quando una congregazione supera il limite massimo di una risposta.
  const paginatedVolontari = allVolontari || [];
  const totalPages = pagination.pages || 1;

  useEffect(() => {
    if (pagination.page > totalPages) {
      setPagination((prev) => ({ ...prev, page: totalPages }));
    }
  }, [pagination.page, totalPages]);

  // Controllo autorizzazione
  if (!isAuthorized) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Accesso Negato
              </h3>
              <p className="text-sm text-red-700 mt-1">
                Solo gli amministratori possono accedere a questa sezione.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-gray-600">Caricamento volontari...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <div
          className={`flex ${
            isMobile ? "flex-col space-y-4" : "items-center justify-between"
          }`}
        >
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Gestione Proclamatori
            </h1>
            <p className="text-gray-600 mt-1">
              Crea, modifica ed elimina i proclamatori del sistema
            </p>
            <div className="accent-line mt-3" />
          </div>
          <div
            className={`flex ${
              isMobile ? "flex-col space-y-3 w-full" : "items-center space-x-3"
            }`}
          >
            <div
              className={`flex ${
                isMobile ? "flex-col space-y-2 w-full" : "items-center space-x-2"
              }`}
            >
              <button
                onClick={() => handleExport("json")}
                className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                  isMobile ? "justify-center w-full" : ""
                }`}
                title="Esporta in JSON"
              >
                <svg
                  className="h-4 w-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                JSON
              </button>
              <button
                onClick={() => handleExport("csv")}
                className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                  isMobile ? "justify-center w-full" : ""
                }`}
                title="Esporta in CSV"
              >
                <svg
                  className="h-4 w-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                CSV
              </button>
              <button
                onClick={() => setShowImportModal(true)}
                className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                  isMobile ? "justify-center w-full" : ""
                }`}
                title="Importa volontari"
              >
                <svg
                  className="h-4 w-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                  />
                </svg>
                Import
              </button>
            </div>

            <button
              onClick={openCreateModal}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                isMobile ? "justify-center w-full" : ""
              }`}
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Nuovo Volontario
            </button>
          </div>
        </div>
      </div>

      {/* Statistiche */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-blue-100">
              <UserIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                {filters.search || filters.stato || filters.sesso
                  ? "Risultati"
                  : "Totali"}
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {summary.totale}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-purple-100">
              <UserIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                {filters.search || filters.stato || filters.sesso
                  ? "Uomini (filtro)"
                  : "Uomini"}
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {summary.uomini}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-pink-100">
              <UserIcon className="h-6 w-6 text-pink-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                {filters.search || filters.stato || filters.sesso
                  ? "Donne (filtro)"
                  : "Donne"}
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {summary.donne}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-green-100">
              <UserIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                {filters.search || filters.stato || filters.sesso
                  ? "Attivi (filtro)"
                  : "Attivi"}
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {summary.attivi}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtri */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="form-label">Stato</label>
            <select
              className="form-input py-2"
              value={filters.stato}
              onChange={(e) => handleFilterChange("stato", e.target.value)}
            >
              <option value="">Tutti</option>
              <option value="attivo">Attivo</option>
              <option value="non_attivo">Non Attivo</option>
            </select>
          </div>
          <div>
            <label className="form-label">Sesso</label>
            <select
              className="form-input py-2"
              value={filters.sesso}
              onChange={(e) => handleFilterChange("sesso", e.target.value)}
            >
              <option value="">Tutti</option>
              <option value="M">Uomini</option>
              <option value="F">Donne</option>
            </select>
          </div>
          <div>
            <label className="form-label">Cerca</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Nome, cognome o email..."
                className="form-input pl-10 py-2"
                value={searchInput}
                onChange={(e) => handleSearch(e.target.value)}
              />
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
          </div>
          <div className="flex items-end space-x-4">
            <div className="text-sm text-gray-600">
              Risultati: {summary.totale} volontari
            </div>
            <div>
              <label className="form-label">Mostra</label>
              <select
                className="form-input py-2"
                value={pagination.limit}
                onChange={(e) => {
                  setPagination((prev) => ({
                    ...prev,
                    limit: parseInt(e.target.value),
                    page: 1,
                  }));
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Lista Volontari */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Gestione Volontari ({summary.totale})
          </h3>
        </div>
        <div className="accent-line mx-6" />
        {isMobile ? (
          <div className="divide-y divide-gray-200">
            {paginatedVolontari.map((volontario) => (
              <div
                key={volontario.id}
                className="relative p-4 pl-6 space-y-3 transition hover:bg-gray-50 hover:shadow-sm"
              >
                <span
                  className="accent-bar top-4 bottom-4"
                  aria-hidden="true"
                />
                <div className="flex items-center">
                  <div className="text-3xl mr-3">
                    {getSessoIcon(volontario.sesso)}
                  </div>
                  <div>
                    <p className="text-base font-semibold text-gray-900">
                      {volontario.nome} {volontario.cognome}
                    </p>
                    <p className="text-xs text-gray-500">ID: {volontario.id}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500 text-xs uppercase">Stato</p>
                    <span
                      className={`inline-flex px-2 py-0.5 mt-1 rounded-full text-xs font-semibold ${getStatusColor(
                        volontario.stato
                      )}`}
                    >
                      {volontario.stato === "attivo" ? "Attivo" : "Non Attivo"}
                    </span>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase">Sesso</p>
                    <p className="mt-1 text-gray-900">
                      {volontario.sesso === "M" ? "Uomo" : "Donna"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase">Ultima assegnazione</p>
                    <p className="mt-1 text-gray-900">
                      {formatDate(volontario.ultima_assegnazione)}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase">Turni completati</p>
                    <p className="mt-1 text-gray-900">
                      {volontario.turni_completati || "0"}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setMobileDetailVolunteer(volontario)}
                    className="flex-1 min-w-[120px] px-3 py-2 text-sm font-medium border border-primary-200 text-primary-700 rounded-md hover:bg-primary-50"
                  >
                    Dettagli
                  </button>
                  <button
                    onClick={() => openEditModal(volontario)}
                    className="px-3 py-2 text-sm font-medium text-indigo-600 border border-indigo-200 rounded-md"
                  >
                    Modifica
                  </button>
                  <button
                    onClick={() => handleDelete(volontario.id)}
                    disabled={deletingId === volontario.id}
                    className="px-3 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-md disabled:opacity-50"
                  >
                    {deletingId === volontario.id ? "..." : "Elimina"}
                  </button>
                </div>
              </div>
            ))}
            {paginatedVolontari.length === 0 && (
              <div className="p-6 text-center text-gray-500 text-sm">
                Nessun volontario trovato per i filtri selezionati.
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header min-w-[150px]">Volontario</th>
                  <th className="table-header">Contatti</th>
                  <th className="table-header">Sesso</th>
                  <th className="table-header">Stato</th>
                  <th className="table-header">Ultima Assegnazione</th>
                  <th className="table-header">Turni Completati</th>
                  <th className="table-header">Ruolo</th>
                  <th className="table-header">Coniuge</th>
                  <th className="table-header">Azioni</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedVolontari.map((volontario) => (
                  <tr key={volontario.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap min-w-[150px] relative pl-6">
                      <span
                        className="accent-bar top-3 bottom-3"
                        aria-hidden="true"
                      />
                      <div className="flex items-center">
                        <div className="text-2xl mr-3">
                          {getSessoIcon(volontario.sesso)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {volontario.nome} {volontario.cognome}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {volontario.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {volontario.email || "N/A"}
                      </div>
                      <div className="text-sm text-gray-500">
                        {volontario.telefono || "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {volontario.sesso === "M" ? "Uomo" : "Donna"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                          volontario.stato
                        )}`}
                      >
                        {volontario.stato === "attivo" ? "Attivo" : "Non Attivo"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(volontario.ultima_assegnazione)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {volontario.turni_completati || "0"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          volontario.ruolo === "super_admin"
                            ? "bg-red-100 text-red-800"
                            : volontario.ruolo === "admin"
                            ? "bg-purple-100 text-purple-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {volontario.ruolo === "super_admin"
                          ? "Super Admin"
                          : volontario.ruolo === "admin"
                          ? "Amministratore"
                          : "Volontario"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {volontario.coniuge_nome ? (
                        <div className="flex items-center text-sm text-gray-900">
                          <HeartIcon className="h-4 w-4 text-pink-500 mr-1" />
                          {volontario.coniuge_nome}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {isAuthorized && (
                          <button
                            onClick={() => handleInvitePush(volontario.id)}
                            className="px-2 py-1 border border-primary-200 text-primary-700 rounded hover:bg-primary-50 text-xs"
                            title="Invita a attivare le push"
                            disabled={invitePushLoadingId === volontario.id}
                          >
                            {invitePushLoadingId === volontario.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                            ) : (
                              "Invita push"
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => openEditModal(volontario)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Modifica"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(volontario.id)}
                          disabled={deletingId === volontario.id}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          title="Elimina"
                        >
                          {deletingId === volontario.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                          ) : (
                            <TrashIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginazione */}
        {pagination.total > pagination.limit && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div
              className={`flex ${
                isMobile
                  ? "flex-col space-y-3 text-sm text-center"
                  : "items-center justify-between"
              }`}
            >
              <div className={`${isMobile ? "" : "text-sm"} text-gray-700`}>
                Mostrando{" "}
                {pagination.total > 0
                  ? (pagination.page - 1) * pagination.limit + 1
                  : 0}{" "}
                a{" "}
                {Math.min(
                  pagination.page * pagination.limit,
                  pagination.total
                )}{" "}
                di {pagination.total} risultati
              </div>
              <div
                className={`flex ${
                  isMobile ? "flex-col space-y-2" : "space-x-2"
                }`}
              >
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className={`px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 ${
                    isMobile ? "w-full" : ""
                  }`}
                >
                  Precedente
                </button>
                <span className="px-3 py-2 text-sm text-gray-700">
                  Pagina {pagination.page} di {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === totalPages}
                  className={`px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 ${
                    isMobile ? "w-full" : ""
                  }`}
                >
                  Successiva
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Dettagli Volontario (solo mobile) */}
      {isMobile && mobileDetailVolunteer && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-md p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Dettagli {mobileDetailVolunteer.nome} {mobileDetailVolunteer.cognome}
              </h3>
              <button
                onClick={() => setMobileDetailVolunteer(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4 text-sm text-gray-700">
              <div>
                <p className="text-xs uppercase text-gray-500">Contatti</p>
                <p>{mobileDetailVolunteer.email || "N/A"}</p>
                <p className="text-gray-500">{mobileDetailVolunteer.telefono || "N/A"}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs uppercase text-gray-500">Ruolo</p>
                  <p>
                    {mobileDetailVolunteer.ruolo === "super_admin"
                      ? "Super Admin"
                      : mobileDetailVolunteer.ruolo === "admin"
                      ? "Amministratore"
                      : "Volontario"}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Stato</p>
                  <span
                    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(
                      mobileDetailVolunteer.stato
                    )}`}
                  >
                    {mobileDetailVolunteer.stato === "attivo" ? "Attivo" : "Non Attivo"}
                  </span>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Ultima Assegnazione</p>
                  <p>{formatDate(mobileDetailVolunteer.ultima_assegnazione)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Turni completati</p>
                  <p>{mobileDetailVolunteer.turni_completati || "0"}</p>
                </div>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500">Coniuge</p>
                {mobileDetailVolunteer.coniuge_nome ? (
                  <div className="flex items-center text-gray-900">
                    <HeartIcon className="h-4 w-4 text-pink-500 mr-1" />
                    {mobileDetailVolunteer.coniuge_nome}
                  </div>
                ) : (
                  <p className="text-gray-500">Nessuno</p>
                )}
              </div>
            </div>
            <div className="mt-6 flex flex-col space-y-2">
              {isAuthorized && (
                <button
                  onClick={() => handleInvitePush(mobileDetailVolunteer.id)}
                  disabled={invitePushLoadingId === mobileDetailVolunteer.id}
                  className="w-full px-4 py-2 text-sm font-medium text-primary-700 border border-primary-200 rounded-md hover:bg-primary-50 disabled:opacity-60"
                  title="Invita ad attivare le notifiche push"
                >
                  {invitePushLoadingId === mobileDetailVolunteer.id ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                      <span>Invio invito...</span>
                    </div>
                  ) : (
                    "Invita alle notifiche push"
                  )}
                </button>
              )}
              <button
                onClick={() => {
                  setMobileDetailVolunteer(null);
                  openEditModal(mobileDetailVolunteer);
                }}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md"
              >
                Modifica volontario
              </button>
              <button
                onClick={() => setMobileDetailVolunteer(null)}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal per creazione/modifica */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-4xl max-h-[85vh] overflow-y-auto shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingVolontario ? "Modifica Volontario" : "Nuovo Volontario"}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Nome *</label>
                    <input
                      type="text"
                      required
                      className="form-input py-2"
                      value={formData.nome}
                      onChange={(e) => handleFormChange("nome", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="form-label">Cognome *</label>
                    <input
                      type="text"
                      required
                      className="form-input py-2"
                      value={formData.cognome}
                      onChange={(e) =>
                        handleFormChange("cognome", e.target.value)
                      }
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-input py-2"
                      value={formData.email}
                      onChange={(e) => handleFormChange("email", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="form-label">Telefono</label>
                    <input
                      type="tel"
                      className="form-input py-2"
                      value={formData.telefono}
                      onChange={(e) =>
                        handleFormChange("telefono", e.target.value)
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="form-label">
                    {editingVolontario
                      ? "Nuova Password (lasciare vuoto per non cambiare)"
                      : "Password *"}
                  </label>
                  <input
                    type="password"
                    className="form-input py-2"
                    value={formData.password}
                    onChange={(e) =>
                      handleFormChange("password", e.target.value)
                    }
                    placeholder={
                      editingVolontario
                        ? "Inserisci nuova password..."
                        : "Inserisci password..."
                    }
                    minLength={editingVolontario ? 0 : 6}
                    required={!editingVolontario}
                  />
                  {editingVolontario && (
                    <p className="text-xs text-gray-500 mt-1">
                      Lascia vuoto per mantenere la password attuale
                    </p>
                  )}
                  {!editingVolontario && (
                    <p className="text-xs text-gray-500 mt-1">
                      Minimo 6 caratteri
                    </p>
                  )}
                </div>
                {!editingVolontario && (
                  <div>
                    <label className="form-label">Conferma Password *</label>
                    <input
                      type="password"
                      className="form-input py-2"
                      value={formData.confermaPassword}
                      onChange={(e) =>
                        handleFormChange("confermaPassword", e.target.value)
                      }
                      placeholder="Conferma la password..."
                      required
                    />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Sesso *</label>
                    <select
                      required
                      className="form-input py-2"
                      value={formData.sesso}
                      onChange={(e) =>
                        handleFormChange("sesso", e.target.value)
                      }
                    >
                      <option value="">Seleziona</option>
                      <option value="M">Uomo</option>
                      <option value="F">Donna</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Stato</label>
                    <select
                      className="form-input py-2"
                      value={formData.stato}
                      onChange={(e) =>
                        handleFormChange("stato", e.target.value)
                      }
                    >
                      <option value="attivo">Attivo</option>
                      <option value="non_attivo">Non Attivo</option>
                    </select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between gap-4 border rounded-lg p-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Postazioni
                      </p>
                      <p className="text-xs text-gray-500">
                        Abilita il proclamatore al servizio nelle postazioni.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={formData.postazioni_attivo}
                        onChange={(e) =>
                          handleFormChange("postazioni_attivo", e.target.checked)
                        }
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:bg-primary-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                    </label>
                  </div>
                  {formData.sesso === "M" && (
                    <div className="flex items-center justify-between gap-4 border rounded-lg p-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Service Leader
                        </p>
                        <p className="text-xs text-gray-500">
                          Abilita il proclamatore a condurre le adunanze per il
                          servizio.
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={formData.service_leader}
                          onChange={(e) =>
                            handleFormChange("service_leader", e.target.checked)
                          }
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:bg-primary-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                      </label>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between gap-4 border rounded-lg p-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Privilegi
                    </p>
                    <p className="text-xs text-gray-500">
                      Gestisci le capabilities del proclamatore.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowPrivilegesModal(true)}
                    className="px-3 py-1.5 text-sm font-medium text-primary-700 border border-primary-200 rounded-md hover:bg-primary-50"
                  >
                    Gestisci{selectedPrivileges.length ? ` (${selectedPrivileges.length})` : ""}
                  </button>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Ruolo</label>
                    <select
                      className="form-input py-2"
                      value={formData.ruolo}
                      onChange={(e) => handleFormChange("ruolo", e.target.value)}
                    >
                      <option value="volontario">Volontario</option>
                      <option value="admin">Amministratore</option>
                    </select>
                  </div>
                  {(user?.ruolo === "admin" || user?.ruolo === "super_admin") && (
                    <div>
                      <label className="form-label">Codice assegnazione</label>
                      <input
                        type="text"
                        className="form-input py-2 bg-gray-100 text-gray-700 cursor-not-allowed"
                        value={
                          assignmentCode
                            ? `${assignmentCode}`
                            : "Nessuna congregazione selezionata"
                        }
                        readOnly
                        disabled
                      />
                      {!assignmentCode && (
                        <p className="text-xs text-red-500 mt-1">
                          {user?.ruolo === "super_admin"
                            ? "Seleziona una congregazione attiva per assegnare il codice."
                            : "Il codice viene assegnato automaticamente dalla tua congregazione."}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className="form-label">
                    <HeartIcon className="h-4 w-4 inline-block mr-1 text-pink-500" />
                    Coniuge (opzionale)
                  </label>
                  <select
                    className="form-input py-2"
                    value={formData.coniuge_id || ""}
                    onChange={(e) =>
                      handleFormChange(
                        "coniuge_id",
                        e.target.value ? parseInt(e.target.value, 10) : null
                      )
                    }
                    disabled={!formData.sesso}
                  >
                    <option value="">
                      {formData.sesso
                        ? "Nessun coniuge"
                        : "Seleziona prima il sesso"}
                    </option>
                    {spouseOptions.map((spouse) => (
                      <option key={spouse.id} value={spouse.id}>
                        {spouse.nome} {spouse.cognome}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Mostra solo volontari di sesso opposto nella stessa congregazione
                  </p>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingVolontario(null);
                      setShowPrivilegesModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                    disabled={saving}
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-60"
                    disabled={saving}
                  >
                    {saving
                      ? "Salvataggio..."
                      : editingVolontario
                      ? "Aggiorna"
                      : "Crea"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {saving && showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-40 z-[70] flex items-center justify-center">
          <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-6 py-4 flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
            <span className="text-sm text-gray-700">
              Salvataggio dei dati in corso...
            </span>
          </div>
        </div>
      )}

      {/* Modal per importazione */}
      {showImportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Importa Volontari
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="form-label">Seleziona File</label>
                  <input
                    type="file"
                    accept=".json,.csv"
                    onChange={handleFileSelect}
                    className="form-input py-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Formati supportati: JSON, CSV (max 5MB)
                  </p>
                </div>
                
                {importFile && (
                  <div className="bg-gray-50 p-3 rounded-md">
                    <p className="text-sm text-gray-700">
                      <strong>File selezionato:</strong> {importFile.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      Dimensione: {(importFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                )}

                <div className="bg-blue-50 p-3 rounded-md">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">
                    Formato richiesto:
                  </h4>
                  <div className="text-xs text-blue-700 space-y-1">
                    <p><strong>JSON:</strong> Array di oggetti con campi: nome, cognome, email, telefono, sesso, stato, ruolo</p>
                    <p><strong>CSV:</strong> Intestazioni: nome,cognome,email,telefono,sesso,stato,ruolo</p>
                    <p><strong>Campi obbligatori:</strong> nome, cognome, sesso</p>
                    <p><strong>Valori sesso:</strong> M o F</p>
                    <p><strong>Valori stato:</strong> attivo o non_attivo</p>
                    <p><strong>Valori ruolo:</strong> volontario o admin</p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowImportModal(false);
                      setImportFile(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                    disabled={importing}
                  >
                    Annulla
                  </button>
                  <button
                    type="button"
                    onClick={handleImport}
                    disabled={!importFile || importing}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {importing ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Importando...
                      </div>
                    ) : (
                      'Importa'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showPrivilegesModal && (
        <div className="fixed inset-0 z-[60] flex items-end bg-gray-600 bg-opacity-60 sm:items-center sm:justify-center sm:p-4">
          <div className="flex max-h-[92dvh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border bg-white shadow-lg sm:rounded-xl">
            <div className="overflow-y-auto p-5 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Privilegi
              </h3>
              <p className="text-sm text-gray-500">
                Le modifiche si salvano quando confermi il volontario.
              </p>
              {!formData.sesso ? (
                <div className="mt-4 text-sm text-gray-600">
                  Seleziona il sesso del volontario per visualizzare i
                  privilegi disponibili.
                </div>
              ) : privilegesLoading || volunteerPrivilegesLoading || !privilegesInitialized ? (
                <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                  Caricamento delle selezioni già salvate...
                </div>
              ) : filteredPrivileges.length ? (
                <div className="mt-4 space-y-3">
                  {filteredPrivileges.map((privilegio) => (
                    <div
                      key={privilegio.key}
                      className="flex items-center justify-between gap-4 border rounded-lg p-3"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {privilegio.label}
                        </p>
                        <p className="text-xs text-gray-500">
                          {privilegio.key === "anziano"
                            ? "Solo uomini · include le capacità del Servitore di Ministero"
                            : privilegio.key === "servitore_ministero"
                            ? "Solo uomini · alternativo ad Anziano"
                            : privilegio.sesso_ammesso === "A"
                            ? "Disponibile per tutti"
                            : privilegio.sesso_ammesso === "M"
                            ? "Solo uomini"
                            : "Solo donne"}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={selectedPrivilegesSet.has(privilegio.key)}
                          onChange={() => togglePrivilege(privilegio.key)}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:bg-primary-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                      </label>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 text-sm text-gray-600">
                  Nessun privilegio disponibile per questo sesso.
                </div>
              )}
            </div>
            <div className="shrink-0 border-t bg-white p-4 sm:px-6">
              {formData.sesso === "M" && privilegesInitialized && (
                <p className="mb-3 text-xs text-gray-500" role="status">
                  {selectedPrivilegesSet.has("anziano")
                    ? "Anziano attivo: le capacità del Servitore di Ministero sono già comprese."
                    : selectedPrivilegesSet.has("servitore_ministero")
                    ? "Servitore di Ministero attivo."
                    : "Nessuna nomina selezionata."}
                </p>
              )}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowPrivilegesModal(false)}
                  className="min-h-11 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Fatto
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestioneVolontari;
